import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormData } from "../AdvisorWizard";
import { Home, Building, MapPin, Wrench, Clock } from "lucide-react";

interface PropertyStepProps {
  data: FormData;
  onComplete: (data: Partial<FormData>) => void;
}

const PropertyStep = ({ data, onComplete }: PropertyStepProps) => {
  const [formData, setFormData] = useState<Partial<FormData>>({
    propertyType: data.propertyType || '',
    estimatedValue: data.estimatedValue || '',
    location: data.location || '',
    condition: data.condition || '',
    livingDuration: data.livingDuration || ''
  });

  const propertyTypes = [
    { value: 'Einfamilienhaus', label: 'Einfamilienhaus', subtitle: 'Freistehendes Haus', icon: Home },
    { value: 'Eigentumswohnung', label: 'Eigentumswohnung', subtitle: 'Wohnung in Mehrfamilienhaus', icon: Building },
    { value: 'Mehrfamilienhaus', label: 'Mehrfamilienhaus', subtitle: 'Vermietungsobjekt', icon: Building },
    { value: 'Sonstiges', label: 'Sonstiges', subtitle: 'Andere Immobilientypen', icon: Home }
  ];

  const valueRanges = [
    { value: 'Unter 200k€', label: 'Unter 200.000 €', subtitle: 'Niedrigpreissegment' },
    { value: '200-400k€', label: '200.000 - 400.000 €', subtitle: 'Mittleres Segment' },
    { value: '400-600k€', label: '400.000 - 600.000 €', subtitle: 'Gehobenes Segment' },
    { value: '600-800k€', label: '600.000 - 800.000 €', subtitle: 'Hochpreissegment' },
    { value: 'Über 800k€', label: 'Über 800.000 €', subtitle: 'Luxussegment' }
  ];

  const locations = [
    { value: 'Großstadt', label: 'Großstadt', subtitle: 'Über 100.000 Einwohner', icon: Building },
    { value: 'Kleinstadt', label: 'Kleinstadt', subtitle: '20.000-100.000 Einwohner', icon: MapPin },
    { value: 'Ländlich', label: 'Ländlich', subtitle: 'Unter 20.000 Einwohner', icon: Home }
  ];

  const conditions = [
    { value: 'Sehr gut', label: 'Sehr gut', subtitle: 'Neuwertig/renoviert' },
    { value: 'Gut', label: 'Gut', subtitle: 'Gepflegt, kleinere Mängel' },
    { value: 'Renovierungsbedürftig', label: 'Renovierungsbedürftig', subtitle: 'Modernisierung nötig' },
    { value: 'Sanierungsbedürftig', label: 'Sanierungsbedürftig', subtitle: 'Umfassende Sanierung nötig' }
  ];

  const durations = [
    { value: 'Unter 10 Jahre', label: 'Unter 10 Jahre', subtitle: 'Kurze Wohndauer' },
    { value: '10-20 Jahre', label: '10-20 Jahre', subtitle: 'Mittlere Wohndauer' },
    { value: '20-30 Jahre', label: '20-30 Jahre', subtitle: 'Lange Wohndauer' },
    { value: 'Über 30 Jahre', label: 'Über 30 Jahre', subtitle: 'Sehr lange Wohndauer' }
  ];

  const handleOptionSelect = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isComplete = () => {
    return formData.propertyType && formData.estimatedValue && formData.location && 
           formData.condition && formData.livingDuration;
  };

  const handleNext = () => {
    if (isComplete()) {
      onComplete(formData);
    }
  };

  return (
    <Card className="card-elevated">
      <CardHeader className="text-center pb-6">
        <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
          <Home className="h-8 w-8 text-accent" />
        </div>
        <CardTitle className="text-2xl md:text-3xl text-primary">
          Schritt 2: Ihre Immobilie
        </CardTitle>
        <p className="text-lg text-muted-foreground">
          Beschreiben Sie Ihre Immobilie, damit wir die passenden Verrentungsoptionen ermitteln können.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-8">
        {/* Immobilientyp */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
            Art Ihrer Immobilie
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {propertyTypes.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('propertyType', option.value)}
                  className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[80px] flex flex-col items-center justify-center ${
                    formData.propertyType === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-accent flex-shrink-0" />
                  <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                  <div className="text-xs text-muted-foreground leading-tight break-words w-full">{option.subtitle}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Geschätzter Wert */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
            Geschätzter Marktwert
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {valueRanges.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('estimatedValue', option.value)}
                className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col justify-center ${
                  formData.estimatedValue === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Lage */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
            Lage der Immobilie
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {locations.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect('location', option.value)}
                  className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[80px] flex flex-col items-center justify-center ${
                    formData.location === option.value
                      ? 'border-accent bg-accent/5 shadow-md'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-accent flex-shrink-0" />
                  <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                  <div className="text-xs text-muted-foreground leading-tight break-words w-full">{option.subtitle}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Zustand */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
            Zustand der Immobilie
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {conditions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('condition', option.value)}
                className={`p-3 sm:p-4 text-left border rounded-lg transition-all hover:shadow-md min-h-[70px] flex flex-col justify-center ${
                  formData.condition === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Wohndauer */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
            <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
            Wie lange wohnen Sie schon dort?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {durations.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect('livingDuration', option.value)}
                className={`p-3 sm:p-4 text-center border rounded-lg transition-all hover:shadow-md min-h-[80px] flex flex-col items-center justify-center ${
                  formData.livingDuration === option.value
                    ? 'border-accent bg-accent/5 shadow-md'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-2 text-accent flex-shrink-0" />
                <div className="font-semibold text-primary text-xs sm:text-sm leading-tight break-words w-full">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-tight break-words w-full">{option.subtitle}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Weiter Button */}
        <div className="pt-6 flex justify-center">
          <Button 
            variant="accent" 
            size="lg" 
            onClick={handleNext}
            disabled={!isComplete()}
            className="text-base sm:text-lg md:text-xl px-6 py-3 sm:px-8 md:px-12 h-auto leading-tight text-center whitespace-normal max-w-xs sm:max-w-sm md:max-w-none"
          >
            <span className="block">
              Weiter zu Schritt 3:<br className="sm:hidden" />
              <span className="sm:ml-1">Ihre Wünsche</span>
            </span>
          </Button>
          {!isComplete() && (
            <p className="text-sm text-muted-foreground mt-2">
              Bitte beantworten Sie alle Fragen, um fortzufahren
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyStep;